import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import JSZip from 'jszip';
import {
  Calendar,
  Camera,
  CheckCircle2,
  ChevronDown,
  Clapperboard,
  Download,
  FileVideo,
  Image as ImageIcon,
  Package,
  PauseCircle,
  PlayCircle,
  RotateCcw,
  ScanSearch,
  ShieldAlert,
  Sparkles,
  Upload,
  Video,
  X,
} from 'lucide-react';
import {
  fetchAllDetections,
  fetchSessionSummary,
  resetSession,
  resolveDetection,
  videoUrl,
} from '../api';
import { addDetection, getSessionSummary, todayKey, updateDetection } from '../localHistory';
import { renderDetectedVideo } from '../detectionRenderer';
import { clearAllArchivedVideos, findArchivedVideoByFilename, saveArchivedVideo } from '../videoArchive';
import {
  CAMERAS,
  CLASS_META,
  SAMPLE_VIDEOS,
  getArchiveDateOptions,
  getArchivePlaylist,
  getScenarioByKey,
  interpolateBox,
  resolveScenarioForFilename,
} from '../scenarios';
import AlertBanner from '../components/AlertBanner';
import DetectionLog from '../components/DetectionLog';
import EvidenceModal from '../components/EvidenceModal';
import StatsSummary from '../components/StatsSummary';

const CCTV_CAMERA_LABEL = 'CCTV-1';
const TICK_INTERVAL_SEC = 1.2;

function StepPill({ n, label, status, icon: Icon }) {
  return (
    <div className={`step-pill step-pill--${status}`}>
      <span className="step-pill__num">{status === 'done' ? <CheckCircle2 size={14} /> : n}</span>
      <Icon size={14} />
      <span className="step-pill__label">{label}</span>
    </div>
  );
}

// A four-step guided walkthrough ("Show me how"): highlights one target
// element at a time with a spotlight cutout + callout tooltip. Purely
// presentational - it never touches app state beyond which step is showing.
function TourOverlay({ step, total, targetEl, title, body, onNext, onSkip, isLast }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!targetEl) return undefined;
    targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const update = () => {
      const r = targetEl.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const settle = setTimeout(update, 280);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      clearTimeout(settle);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [targetEl, step]);

  if (!rect) return null;

  const pad = 8;
  const spotlightStyle = {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };

  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  const showBelow = rect.top + rect.height + 190 < viewportH;
  const tooltipTop = showBelow ? rect.top + rect.height + pad + 12 : Math.max(16, rect.top - 168);
  const tooltipLeft = Math.max(16, Math.min(rect.left, viewportW - 336));

  return (
    <div className="tour-layer">
      <div className="tour-backdrop" onClick={onSkip} role="presentation" />
      <div className="tour-spotlight" style={spotlightStyle} />
      <div className="tour-tooltip" style={{ top: tooltipTop, left: tooltipLeft }}>
        <button type="button" className="tour-tooltip__close" onClick={onSkip} title="Close walkthrough">
          <X size={13} />
        </button>
        <span className="tour-tooltip__step">STEP {step} OF {total}</span>
        <h4>{title}</h4>
        <p>{body}</p>
        <div className="tour-tooltip__actions">
          <button type="button" className="ghost-btn" onClick={onSkip}>Skip</button>
          <button type="button" className="primary-btn" onClick={onNext}>{isLast ? 'Got it' : 'Next'}</button>
        </div>
      </div>
    </div>
  );
}

function captureFrame(video) {
  try {
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
    return dataUrl.split(',')[1] || null;
  } catch {
    return null;
  }
}

// Maps a fractional bbox (0-1 of the VIDEO FRAME) to on-screen pixels inside
// the preview container, accounting for the crop that "object-fit: cover"
// applies when the video's aspect ratio doesn't match the container's.
function computeBoxStyle(video, container, bbox) {
  if (!video || !container || !bbox || !video.videoWidth || !video.videoHeight) return null;
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const scale = Math.max(cw / vw, ch / vh);
  const rw = vw * scale;
  const rh = vh * scale;
  const ox = (cw - rw) / 2;
  const oy = (ch - rh) / 2;
  return {
    left: `${ox + bbox.x * rw}px`,
    top: `${oy + bbox.y * rh}px`,
    width: `${bbox.w * rw}px`,
    height: `${bbox.h * rh}px`,
  };
}

// The date dropdown's value is the literal VIDEO_PLAYLIST key ('today',
// '01-04-2026', ...) - only 'today' needs resolving to a real dd-mm-yyyy
// date before it's used to tag/store a detection row.
function resolveStorageDate(dateKey) {
  return dateKey === 'today' ? todayKey() : dateKey;
}

export default function Dashboard() {
  const dateOptions = useMemo(() => getArchiveDateOptions(), []);

  const [sourceType, setSourceType] = useState(null); // 'cctv' | 'video'
  const [activeVideoKey, setActiveVideoKey] = useState(null);
  const [activeVideoSrc, setActiveVideoSrc] = useState(null);
  const [activeVideoLabel, setActiveVideoLabel] = useState('');
  const [selectedCamera, setSelectedCamera] = useState(CAMERAS[0]);
  const [selectedArchiveDate, setSelectedArchiveDate] = useState(() => dateOptions[0].key);
  const [logDate, setLogDate] = useState(() => resolveStorageDate(dateOptions[0].key));
  const [isRunning, setIsRunning] = useState(false);
  const [runComplete, setRunComplete] = useState(false);
  const [confidence, setConfidence] = useState(35);
  const [summary, setSummary] = useState(null);
  const [detections, setDetections] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [lastAlert, setLastAlert] = useState(null);
  const [evidenceId, setEvidenceId] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [zippingVideos, setZippingVideos] = useState(false);
  const [downloadingVideoKey, setDownloadingVideoKey] = useState(null);
  const [renderingVideo, setRenderingVideo] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);
  const [clockTick, setClockTick] = useState(() => new Date());
  const [activeBoxes, setActiveBoxes] = useState([]);
  const [runDefectTotal, setRunDefectTotal] = useState(0);
  const [cameraNotice, setCameraNotice] = useState(null);
  const [tourStep, setTourStep] = useState(0);
  const [cameraOn, setCameraOn] = useState(false);

  const videoRef = useRef(null);
  const feedShellRef = useRef(null);
  const fileInputRef = useRef(null);
  const engineRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const uploadedUrlRef = useRef(null);
  const downloadMenuRef = useRef(null);
  const cameraStepRef = useRef(null);
  const dateStepRef = useRef(null);
  const clipStepRef = useRef(null);
  const startBtnRef = useRef(null);

  const soundRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastBeepAtRef = useRef(0);
  const archiveInFlightRef = useRef(new Map());

  const isVideoSource = sourceType === 'video';
  const isCctvSource = sourceType === 'cctv';
  const hasSource = isVideoSource || isCctvSource;

  // Session-wide, not date-filtered: the Detection Log / Live Detection Feed
  // are meant to keep showing everything logged since the page last loaded,
  // no matter which camera/date/clip gets picked afterwards. Only "Clear
  // session log", a real page refresh, or signing out should ever empty it.
  const refreshData = useCallback(async () => {
    setLoadingLog(true);
    try {
      const [summaryRes, detectionsRes] = await Promise.all([
        fetchSessionSummary(),
        fetchAllDetections(150),
      ]);
      setSummary(summaryRes.data);
      setDetections(detectionsRes.data?.detections || []);
    } finally {
      setLoadingLog(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!isRunning) return undefined;
    const interval = setInterval(() => setClockTick(new Date()), 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  useEffect(() => () => {
    audioContextRef.current?.close().catch(() => {});
    if (uploadedUrlRef.current) URL.revokeObjectURL(uploadedUrlRef.current);
    webcamStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const ensureAudioContext = async () => {
    try {
      if (!audioContextRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) audioContextRef.current = new AudioCtx();
      }
      if (audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }
    } catch {
      // ignore audio init issues
    }
  };

  const playBeepFallback = () => {
    const ctx = audioContextRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    const beepLength = 0.14;
    const pause = 0.08;

    [0, 1].forEach((index) => {
      const start = now + index * (beepLength + pause);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = index === 0 ? 880 : 1040;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + beepLength);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + beepLength + 0.01);
    });
  };

  const playContaminationAlert = () => {
    const now = Date.now();
    if (now - lastBeepAtRef.current < 1200) return;
    lastBeepAtRef.current = now;

    try {
      if (!soundRef.current) {
        soundRef.current = new Audio('/alert.mp3');
        soundRef.current.volume = 0.55;
      }
      soundRef.current.currentTime = 0;
      soundRef.current.play().catch(playBeepFallback);
    } catch {
      playBeepFallback();
    }
  };

  const stopWebcam = () => {
    webcamStreamRef.current?.getTracks().forEach((track) => track.stop());
    webcamStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  };

  const teardownActiveRun = () => {
    engineRef.current = null;
    if (videoRef.current) {
      videoRef.current.pause();
    }
    stopWebcam();
    setIsRunning(false);
    setActiveBoxes([]);
  };

  const setActiveVideoFromKey = (key) => {
    const meta = SAMPLE_VIDEOS.find((v) => v.key === key);
    if (!meta) return null;
    setActiveVideoKey(key);
    setActiveVideoSrc(videoUrl(meta.file));
    setActiveVideoLabel(meta.file);
    return meta;
  };

  const handleSelectCctv = () => {
    teardownActiveRun();
    if (uploadedUrlRef.current) {
      URL.revokeObjectURL(uploadedUrlRef.current);
      uploadedUrlRef.current = null;
    }
    setSourceType('cctv');
    setActiveVideoKey(null);
    setActiveVideoSrc(null);
    setActiveVideoLabel('This device webcam');
    setSelectedCamera(CAMERAS[0]);
    setSelectedArchiveDate(dateOptions[0].key);
    setLogDate(todayKey());
    setRunComplete(false);
    setCameraNotice(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Uploads are locked to the official sample clips (matched by exact
  // filename) - this is a scripted demo with no real model behind it, so an
  // arbitrary video would either show nothing or a made-up fallback result.
  // Rejecting anything else keeps the demo honest about what it can do.
  const handleFileChosen = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const baseName = file.name.trim().toLowerCase();
    const isKnownSample = SAMPLE_VIDEOS.some((v) => v.file.toLowerCase() === baseName);
    if (!isKnownSample) {
      toast.error('Only the official sample clips can be uploaded here. Grab one from "Download sample videos" first.');
      event.target.value = '';
      return;
    }
    teardownActiveRun();
    if (uploadedUrlRef.current) URL.revokeObjectURL(uploadedUrlRef.current);
    const url = URL.createObjectURL(file);
    uploadedUrlRef.current = url;
    setSourceType('video');
    setActiveVideoKey(`upload:${file.name}`);
    setActiveVideoSrc(url);
    setActiveVideoLabel(file.name);
    setSelectedCamera(CAMERAS[0]);
    setSelectedArchiveDate(dateOptions[0].key);
    setLogDate(todayKey());
    setRunComplete(false);
    setCameraNotice(null);
    toast.success(`Loaded "${file.name}" for detection`);
  };

  // Quick Demo chips only ever show the playlist for whatever camera/date is
  // currently selected (see the archive-row controls) - picking one loads
  // just that single clip. Detection always stops when the clip ends; it
  // does not auto-advance into another video.
  const handleSelectPlaylistClip = (key) => {
    teardownActiveRun();
    if (uploadedUrlRef.current) {
      URL.revokeObjectURL(uploadedUrlRef.current);
      uploadedUrlRef.current = null;
    }
    if (!setActiveVideoFromKey(key)) return;
    setSourceType('video');
    setRunComplete(false);
    setCameraNotice(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Camera + date "archive" browser: switching either control loads that
  // combination's first clip (mirroring backend VIDEO_PLAYLIST, which lists
  // several clips per camera per day) - Quick Demo below re-renders to show
  // every clip for this combination so the rest can be picked individually.
  const handleArchiveChange = (camera, date) => {
    const playlist = getArchivePlaylist(date, camera);
    if (!playlist.length) {
      toast.error('No recorded footage for this camera/date combination');
      return;
    }
    teardownActiveRun();
    if (uploadedUrlRef.current) {
      URL.revokeObjectURL(uploadedUrlRef.current);
      uploadedUrlRef.current = null;
    }
    if (!setActiveVideoFromKey(playlist[0])) return;
    setSourceType('video');
    setSelectedCamera(camera);
    setSelectedArchiveDate(date);
    setLogDate(resolveStorageDate(date));
    setRunComplete(false);
    setCameraNotice(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Stamps the CURRENT time-of-day onto the ARCHIVED date, so browsing
  // "Yesterday" footage logs entries under yesterday's date/time instead of
  // today's - keeping the alert banner, log rows, and History page consistent.
  const composeAbsTimestamp = (dateKey, timeSource) => {
    const [dd, mm, yyyy] = (dateKey || '').split('-').map(Number);
    if (!dd || !mm || !yyyy) return timeSource.toLocaleString('en-GB');
    const combined = new Date(yyyy, mm - 1, dd, timeSource.getHours(), timeSource.getMinutes(), timeSource.getSeconds());
    return combined.toLocaleString('en-GB');
  };

  const buildDetectionRow = (incident, camera, date) => {
    const now = new Date();
    const timestamp = now.toLocaleTimeString('en-GB');
    const timestampAbs = composeAbsTimestamp(date, now);
    return {
      camera,
      date,
      timestamp,
      class_name: incident.class_name,
      confidence: incident.baseConfidence,
      max_confidence: incident.baseConfidence,
      product_id: incident.product_id,
      batch_id: incident.batch_id,
      product_name: incident.product_name,
      line_id: incident.line_id,
      shift: incident.shift,
      mfg_date: incident.mfg_date,
      expiry_date: incident.expiry_date,
      unit_number: incident.unit_number,
      qr_payload: null,
      first_seen: timestamp,
      last_seen: timestamp,
      first_seen_at: timestampAbs,
      last_seen_at: timestampAbs,
      duration_sec: 0,
      occurrences: 1,
      resolved: false,
    };
  };

  const handleTimeUpdate = () => {
    const engine = engineRef.current;
    const video = videoRef.current;
    const container = feedShellRef.current;
    if (!engine || !video || !Number.isFinite(video.duration)) return;

    const t = video.currentTime;
    const duration = video.duration;
    const boxes = [];

    if (engine.cameraWarning && !engine.cameraWarningFired) {
      const warnStart = engine.cameraWarning.startFraction * duration;
      const warnEnd = engine.cameraWarning.endFraction * duration;
      if (t >= warnStart && t <= warnEnd) {
        engine.cameraWarningFired = true;
        const notice = { camera: engine.camera, message: engine.cameraWarning.message };
        setCameraNotice(notice);
        toast(notice.message, { icon: '⚠️' });
      }
    }

    engine.incidents.forEach((incident, idx) => {
      const state = engine.states[idx];
      const startSec = incident.startFraction * duration;
      const endSec = incident.endFraction * duration;
      if (t < startSec) return;

      const progress = Math.min(1, Math.max(0, (t - startSec) / Math.max(0.01, endSec - startSec)));
      const liveConfidence = Math.round((incident.baseConfidence + (incident.maxConfidence - incident.baseConfidence) * progress) * 10) / 10;

      if (!state.rowId) {
        if (incident.baseConfidence < engine.threshold) {
          state.suppressed = true;
          return;
        }
        const evidence = captureFrame(video);
        const row = { ...buildDetectionRow(incident, engine.camera, engine.logDate), evidence };
        const saved = addDetection(row);
        state.rowId = saved.id;
        state.lastTick = t;
        setDetections((prev) => [saved, ...prev]);
        setSummary(getSessionSummary());
        setRunDefectTotal((prev) => prev + 1);
        const alertPayload = { camera: engine.camera, detections: [{ class: incident.class_name }], timestamp: composeAbsTimestamp(engine.logDate, new Date()) };
        setLastAlert(alertPayload);
        playContaminationAlert();
      } else if (t <= endSec && t - state.lastTick >= TICK_INTERVAL_SEC) {
        state.lastTick = t;
        const patch = {
          confidence: liveConfidence,
          max_confidence: Math.max(liveConfidence, incident.baseConfidence),
          occurrences: (state.occurrences || 1) + 1,
          duration_sec: Math.round((t - startSec) * 10) / 10,
          last_seen: new Date().toLocaleTimeString('en-GB'),
          last_seen_at: composeAbsTimestamp(engine.logDate, new Date()),
        };
        state.occurrences = patch.occurrences;
        updateDetection(state.rowId, patch);
        setDetections((prev) => prev.map((row) => (row.id === state.rowId ? { ...row, ...patch } : row)));
      }

      if (state.rowId && !state.suppressed && t <= endSec && incident.bbox) {
        const box = interpolateBox(incident.bbox, incident.bboxEnd, progress);
        const style = computeBoxStyle(video, container, box);
        if (style) {
          const meta = CLASS_META[incident.class_name] || { label: incident.class_name, color: 'var(--danger)' };
          boxes.push({ key: `${idx}`, style, label: `${meta.label} ${Math.round(liveConfidence)}%` });
        }
      }
    });

    setActiveBoxes(boxes);
  };

  const resolveScenario = (key, label) => (
    key?.startsWith('upload:') ? resolveScenarioForFilename(label) : getScenarioByKey(key)
  );

  const buildEngine = (scenario, camera, date) => {
    const incidents = scenario?.incidents || [];
    return {
      incidents,
      states: incidents.map(() => ({ rowId: null, lastTick: 0, occurrences: 1 })),
      threshold: confidence,
      camera,
      logDate: date,
      cameraWarning: scenario?.cameraWarning || null,
      cameraWarningFired: false,
    };
  };

  // Shared by the automatic archive-on-completion below and the manual
  // "Download Detected Video" button: renders the clip with boxes burned
  // in and stores it once per CAM_Date_ProductID filename. If that exact
  // filename is already archived (e.g. this run already auto-archived it
  // moments ago), it skips re-rendering and just hands back the stored
  // blob - so clicking the button right after a run finishes is instant
  // instead of re-doing 5+ seconds of work for an identical result.
  const renderAndArchive = (incidents, camera, date, { onProgress } = {}) => {
    const productId = incidents[0]?.product_id || 'UNKNOWN';
    const className = CLASS_META[incidents[0]?.class_name]?.label || incidents[0]?.class_name || 'Detection';
    const filename = `${camera}_${date}_${productId}.webm`;

    // The automatic archive-on-completion and a manual "Download Detected
    // Video" click can both fire for the same clip within moments of each
    // other. Without this, they'd race - both see nothing saved yet, and
    // both render+save independently, leaving two near-identical entries.
    // Sharing the in-flight promise means the second caller just waits for
    // the first one's result instead of duplicating the work.
    if (archiveInFlightRef.current.has(filename)) {
      return archiveInFlightRef.current.get(filename);
    }

    const task = (async () => {
      const existing = await findArchivedVideoByFilename(filename).catch(() => null);
      if (existing) {
        return { filename, blob: existing.blob, alreadyArchived: true };
      }

      const { blob, thumbnail } = await renderDetectedVideo({
        src: activeVideoSrc,
        incidents,
        classMeta: CLASS_META,
        onProgress,
      });

      await saveArchivedVideo({ filename, camera, date, productId, className, mimeType: blob.type, blob, thumbnail });
      return { filename, blob, alreadyArchived: false };
    })();

    archiveInFlightRef.current.set(filename, task);
    task.finally(() => archiveInFlightRef.current.delete(filename));
    return task;
  };

  const handleVideoEnded = () => {
    setIsRunning(false);
    setRunComplete(true);
    setActiveBoxes([]);
    if (runDefectTotal > 0) {
      toast.success(`Detection run complete — ${runDefectTotal} defect${runDefectTotal > 1 ? 's' : ''} found`);

      // Every clip that actually logs a defect gets archived automatically,
      // so Historical Saved Videos always matches what's in the Detection
      // Log - not just the ones someone remembered to click "download" for.
      const scenario = resolveScenario(activeVideoKey, activeVideoLabel);
      const incidents = scenario?.incidents || [];
      if (incidents.length) {
        renderAndArchive(incidents, overlayCamera, logDate)
          .then(({ alreadyArchived }) => {
            if (!alreadyArchived) toast.success('Saved to Historical Saved Videos', { duration: 2500 });
          })
          .catch(() => {
            // Archiving is a bonus on top of the live demo, not required
            // for it to work - stay quiet if the browser can't record video.
          });
      }
    } else {
      toast.success('Detection run complete — line looks clean');
    }
  };

  // Turning the camera on is now a separate, explicit step from starting
  // detection - previously "Start Detection" silently requested the webcam
  // itself, which read as detection starting instantly with no camera step.
  const handleTurnOnCamera = async () => {
    await ensureAudioContext();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      webcamStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
      toast.success('Camera turned on — hit Start Detection when you\'re ready.');
    } catch (err) {
      toast.error('Could not access the webcam. Check browser camera permission.');
    }
  };

  const handleStart = async () => {
    if (!hasSource) return;
    await ensureAudioContext();

    if (isCctvSource) {
      if (!cameraOn) {
        toast.error('Turn on the camera first.');
        return;
      }
      engineRef.current = null;
      setIsRunning(true);
      setRunComplete(false);
      toast.success('Detection started — live preview only, detection alerts are not triggered in this mode.');
      return;
    }

    if (isVideoSource) {
      const scenario = resolveScenario(activeVideoKey, activeVideoLabel);
      engineRef.current = buildEngine(scenario, selectedCamera, logDate);
      setRunDefectTotal(0);
      setCameraNotice(null);
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        try {
          await videoRef.current.play();
        } catch {
          toast.error('Unable to start video playback.');
          return;
        }
      }
      setIsRunning(true);
      setRunComplete(false);
      toast.success(`Detection started on ${activeVideoLabel}`);
    }
  };

  const handleStop = () => {
    teardownActiveRun();
    toast('Detection stopped');
  };

  // The only three things allowed to wipe the session view: this button, a
  // real page refresh, and signing out (both of the latter re-run the
  // module-level IndexedDB/localStorage resets in videoArchive.js and
  // localHistory.js on their own). Clearing here resets both the detection
  // log AND the video archive together, so "session" means the same thing
  // in both places.
  const handleClearSession = async () => {
    try {
      await resetSession();
      await clearAllArchivedVideos().catch(() => {});
      setLastAlert(null);
      setCameraNotice(null);
      await refreshData();
      toast.success('Cleared session log and saved videos');
    } catch {
      toast.error('Unable to clear session');
    }
  };

  const handleResolve = async (id) => {
    if (!id) {
      refreshData();
      return;
    }
    try {
      await resolveDetection(id);
      toast.success(`Detection #${id} resolved`);
      refreshData();
    } catch {
      toast.error('Failed to resolve');
    }
  };

  const handleTestAlarm = () => {
    toast.success('Test alert acknowledged — camera link and spill sensors are nominal.');
  };

  // Bundles every sample clip into a single .zip, built entirely in the
  // browser (fetch each file as a blob, hand it to JSZip, generate the
  // archive) - there's no backend to zip them server-side in this build.
  const handleDownloadAllZip = async () => {
    if (zippingVideos) return;
    setZippingVideos(true);
    const toastId = toast.loading(`Bundling ${SAMPLE_VIDEOS.length} sample videos into a ZIP…`);
    try {
      const zip = new JSZip();
      await Promise.all(SAMPLE_VIDEOS.map(async (v) => {
        const response = await fetch(videoUrl(v.file));
        if (!response.ok) throw new Error(`Could not fetch ${v.file}`);
        zip.file(v.file, await response.blob());
      }));
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'fsqa-sample-videos.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success('All sample videos downloaded as a ZIP', { id: toastId });
      setShowDownloadMenu(false);
    } catch {
      toast.error('Could not build the ZIP — try downloading clips individually instead.', { id: toastId });
    } finally {
      setZippingVideos(false);
    }
  };

  // A plain `<a href=... download>` only forces a save-as when the URL is
  // same-origin - now that clips are streamed from the backend's own origin
  // (see videoUrl() in api.js), the browser would just navigate to/preview
  // the file instead of downloading it. Fetching the bytes ourselves and
  // handing them to a blob: URL keeps the one-click download working
  // exactly like it did when the files were same-origin static assets.
  const handleDownloadSingleVideo = async (v) => {
    if (downloadingVideoKey) return;
    setDownloadingVideoKey(v.key);
    try {
      const response = await fetch(videoUrl(v.file));
      if (!response.ok) throw new Error(`Could not fetch ${v.file}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = v.file;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      setShowDownloadMenu(false);
    } catch {
      toast.error(`Could not download ${v.file}.`);
    } finally {
      setDownloadingVideoKey(null);
    }
  };

  // Re-renders the currently loaded clip off-screen with the scripted boxes
  // burned into the pixels (there's no backend to do this server-side) and
  // downloads the result renamed CAM_Date_ProductID - see
  // detectionRenderer.js for how the actual capture works. Also stashes a
  // copy in IndexedDB so it shows up later in History's saved-videos panel.
  const handleDownloadDetectedVideo = async () => {
    if (renderingVideo || !isVideoSource || !activeVideoSrc) return;
    const scenario = resolveScenario(activeVideoKey, activeVideoLabel);
    const incidents = scenario?.incidents || [];
    if (!incidents.length) {
      toast.error('This clip has no scripted defect to draw boxes for.');
      return;
    }

    setRenderingVideo(true);
    setRenderProgress(0);
    const toastId = toast.loading('Rendering detected video (0%)…');
    try {
      const { filename, blob } = await renderAndArchive(incidents, overlayCamera, logDate, {
        onProgress: (pct) => {
          setRenderProgress(pct);
          toast.loading(`Rendering detected video (${pct}%)…`, { id: toastId });
        },
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      toast.success(`Downloaded ${filename}`, { id: toastId });
    } catch (err) {
      toast.error(err?.message || 'Could not render this video.', { id: toastId });
    } finally {
      setRenderingVideo(false);
    }
  };

  const handleConfidence = (value) => {
    setConfidence(value);
    if (engineRef.current) engineRef.current.threshold = value;
  };

  const overlayCamera = isCctvSource ? CCTV_CAMERA_LABEL : selectedCamera;
  const overlayLabel = isCctvSource ? `${overlayCamera} · LIVE` : `${overlayCamera} · ${activeVideoLabel}`;
  const overlayDateText = isCctvSource ? clockTick.toLocaleDateString('en-GB') : logDate;
  const overlayTime = clockTick.toLocaleTimeString('en-GB');

  const currentPlaylist = getArchivePlaylist(selectedArchiveDate, selectedCamera)
    .map((key) => SAMPLE_VIDEOS.find((v) => v.key === key))
    .filter(Boolean);
  const selectedDateLabel = dateOptions.find((d) => d.key === selectedArchiveDate)?.label || selectedArchiveDate;
  const isUploadSource = isVideoSource && Boolean(activeVideoKey?.startsWith('upload:'));
  const hasChosenClip = isVideoSource && Boolean(activeVideoKey) && !isUploadSource;
  const stepStatus = {
    clip: hasChosenClip ? 'done' : 'current',
    detect: runComplete ? 'done' : isRunning ? 'current' : hasSource ? 'current' : 'upcoming',
  };

  const tourSteps = [
    { ref: cameraStepRef, title: 'Step 1 — Pick a camera', body: 'Choose which processing line camera to pull footage from.' },
    { ref: dateStepRef, title: 'Step 2 — Pick a date', body: 'Browse footage by day — today, or a past archive date.' },
    { ref: clipStepRef, title: 'Step 3 — Choose a clip', body: 'Every sample clip has a defect hidden in it for the AI to catch.' },
    { ref: startBtnRef, title: 'Step 4 — Start Detection', body: 'Hit Start Detection and watch the AI flag it live, right here.' },
  ];
  const activeTourStep = tourStep > 0 ? tourSteps[tourStep - 1] : null;

  const latestEvidence = detections.find((row) => row.evidence);

  return (
    <div className="page-grid">
      <section className="workspace-header">
        <div>
          <span className="workspace-header__eyebrow">
            <Sparkles size={13} /> DEMO GUIDE
          </span>
          <h2>Let&apos;s run a live detection</h2>
          <p>New here? Pick a camera, pick a date, choose a clip, then hit Start Detection — the AI will catch the defect hiding in that clip, live.</p>
        </div>
        <div className="workspace-header__actions">
          <button type="button" className="tour-trigger" onClick={() => setTourStep(1)}>
            <PlayCircle size={15} /> Show me how
          </button>
          <div className="download-menu" ref={downloadMenuRef}>
            <button type="button" className="ghost-btn" onClick={() => setShowDownloadMenu((prev) => !prev)}>
              <Download size={15} /> Download sample videos <ChevronDown size={13} />
            </button>
            {showDownloadMenu && (
              <div className="download-menu__pop">
                <button
                  type="button"
                  className="download-menu__all"
                  onClick={handleDownloadAllZip}
                  disabled={zippingVideos}
                >
                  <Package size={15} />
                  {zippingVideos ? 'Zipping videos…' : `Download all ${SAMPLE_VIDEOS.length} as .zip`}
                </button>
                <div className="download-menu__divider" />
                {SAMPLE_VIDEOS.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => handleDownloadSingleVideo(v)}
                    disabled={Boolean(downloadingVideoKey)}
                    className="download-menu__item"
                  >
                    {downloadingVideoKey === v.key ? `Downloading ${v.file}…` : v.file} <span>{v.subtitle}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="source-grid">
        <button
          type="button"
          className={`source-card ${isCctvSource ? 'source-card--active' : ''}`}
          onClick={handleSelectCctv}
        >
          <span className="source-card__icon source-card__icon--cctv"><Camera size={15} /></span>
          <span className="source-card__body">
            <strong>Connect with CCTV</strong>
            <small>Uses this device&apos;s webcam for the demo</small>
          </span>
          <span className="source-card__tag">{isCctvSource ? 'Selected' : 'Select'}</span>
        </button>

        <div className={`source-card source-card--static ${isUploadSource ? 'source-card--active' : ''}`}>
          <span className="source-card__icon source-card__icon--upload"><Upload size={15} /></span>
          <span className="source-card__body">
            <strong>Upload recorded video</strong>
            <small>MP4, MOV or WebM · processed locally</small>
          </span>
          <span className="source-card__tag">{isUploadSource ? 'Selected' : 'Select'}</span>
        </div>
        <button type="button" className="source-card__choose" onClick={() => fileInputRef.current?.click()}>
          Choose video
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={handleFileChosen}
          style={{ display: 'none' }}
        />
      </section>

      <div className="workspace-layout">
        <aside className="workspace-sidebar">
          <div className="sidebar-step sidebar-step--camera" ref={cameraStepRef}>
            <div className="sidebar-step__head">
              <span className="sidebar-step__num">1</span>
              <h3><Camera size={14} /> Camera</h3>
            </div>
            <div className="camera-toggle camera-toggle--stack">
              {CAMERAS.map((cam) => (
                <button
                  key={cam}
                  type="button"
                  className={`camera-toggle__btn ${selectedCamera === cam ? 'camera-toggle__btn--active' : ''}`}
                  onClick={() => handleArchiveChange(cam, selectedArchiveDate)}
                >
                  <Camera size={15} /> {cam}
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-step sidebar-step--date" ref={dateStepRef}>
            <div className="sidebar-step__head">
              <span className="sidebar-step__num">2</span>
              <h3><Calendar size={14} /> Date</h3>
            </div>
            <div className="date-toggle date-toggle--stack">
              {dateOptions.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  className={`date-toggle__btn ${selectedArchiveDate === d.key ? 'date-toggle__btn--active' : ''}`}
                  onClick={() => handleArchiveChange(selectedCamera, d.key)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-step sidebar-step--clip" ref={clipStepRef}>
            <div className="sidebar-step__head">
              <span className="sidebar-step__num">3</span>
              <h3><Clapperboard size={14} /> Clip</h3>
            </div>
            <span className="sidebar-step__hint">{selectedCamera} · {selectedDateLabel}</span>
            <div className="clip-grid clip-grid--stack">
              {currentPlaylist.length === 0 && (
                <span className="archive-row__hint">No recorded footage for this camera/date combination.</span>
              )}
              {currentPlaylist.map((v) => {
                const isActive = activeVideoKey === v.key;
                return (
                  <button
                    key={v.key}
                    type="button"
                    className={`clip-card ${isActive ? 'clip-card--active' : ''}`}
                    onClick={() => handleSelectPlaylistClip(v.key)}
                  >
                    <span className="clip-card__icon"><Video size={16} /></span>
                    <span className="clip-card__body">
                      <strong>{v.file}</strong>
                    </span>
                    {isActive && <CheckCircle2 size={17} className="clip-card__check" />}
                  </button>
                );
              })}
            </div>
          </div>

        </aside>

        <div className="workspace-main">
          <section className="step-rail">
            <StepPill n={1} label="Camera" status="done" icon={Camera} />
            <span className="step-rail__line" />
            <StepPill n={2} label="Date" status="done" icon={Calendar} />
            <span className="step-rail__line" />
            <StepPill n={3} label="Clip" status={stepStatus.clip} icon={Clapperboard} />
            <span className="step-rail__line" />
            <StepPill n={4} label="Detect" status={stepStatus.detect} icon={ScanSearch} />
          </section>

          <section className="detection-grid">
            <div className="preview-panel">
              <div ref={feedShellRef} className={`feed-shell ${lastAlert && isRunning ? 'feed-shell--alert' : ''}`}>
            {hasSource && (
              <>
                <div className="feed-shell__meta">
                  <span>{overlayLabel}</span>
                  <span>{overlayDateText}</span>
                  <span>{overlayTime}</span>
                </div>
                <div className={`feed-shell__rec ${isCctvSource && !cameraOn ? 'feed-shell__rec--off' : ''}`}>
                  <span className="rec-dot" />
                  {isCctvSource && !cameraOn
                    ? 'CAMERA OFF'
                    : isRunning
                      ? (isCctvSource ? 'LIVE · DETECTING' : 'LIVE CCTV · DETECTING')
                      : 'LIVE CCTV FEED'}
                </div>
              </>
            )}
            {isVideoSource ? (
              <video
                ref={videoRef}
                className="feed-video"
                src={activeVideoSrc}
                // Sample clips now stream from the backend's own origin
                // (see videoUrl() in api.js) - crossOrigin is required so
                // captureFrame()'s canvas.drawImage()/toDataURL() calls
                // don't throw on a "tainted canvas" for those clips. It's a
                // no-op for uploaded files (blob: URLs) and is ignored for
                // the CCTV feed, which uses its own <video> below instead.
                crossOrigin="anonymous"
                muted
                playsInline
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnded}
              />
            ) : isCctvSource ? (
              <video ref={videoRef} className="feed-video" muted playsInline autoPlay />
            ) : (
              <div className="feed-placeholder">
                <Video size={34} />
                <p>Choose a camera or video to begin.</p>
              </div>
            )}
            {activeBoxes.map((box) => (
              <div key={box.key} className="detection-box" style={box.style}>
                <span className="detection-box__label">{box.label}</span>
              </div>
            ))}
          </div>

          <div className="preview-panel__controls">
            {isCctvSource && !cameraOn ? (
              <button ref={startBtnRef} type="button" className="primary-btn" onClick={handleTurnOnCamera}>
                <Camera size={15} /> Turn on Camera
              </button>
            ) : !isRunning ? (
              <button ref={startBtnRef} type="button" className="primary-btn" onClick={handleStart} disabled={!hasSource}>
                <PlayCircle size={15} /> Start Detection
              </button>
            ) : (
              <button type="button" className="danger-btn" onClick={handleStop}>
                <PauseCircle size={15} /> Stop
              </button>
            )}
            <button type="button" className="ghost-btn" onClick={handleClearSession}>
              <RotateCcw size={15} /> Clear session log
            </button>
            <div className="confidence-slider confidence-slider--inline">
              <span>Sensitivity</span>
              <input
                type="range"
                min="10"
                max="95"
                step="1"
                value={confidence}
                onChange={(e) => handleConfidence(Number(e.target.value))}
              />
              <strong>{confidence}%</strong>
            </div>
          </div>

          {isVideoSource && activeVideoSrc && runComplete && (
            <button
              type="button"
              className="download-detected-btn"
              onClick={handleDownloadDetectedVideo}
              disabled={renderingVideo}
            >
              <FileVideo size={15} />
              {renderingVideo ? `Rendering detected video (${renderProgress}%)…` : 'Download Detected Video'}
            </button>
          )}

          {latestEvidence && (
            <div className="evidence-strip">
              <div className="evidence-strip__head">
                <span><ImageIcon size={13} /> Latest Evidence Snapshot</span>
                <span className="evidence-strip__time">{latestEvidence.timestamp}</span>
              </div>
              <div className="evidence-strip__body">
                <img src={`data:image/jpeg;base64,${latestEvidence.evidence}`} alt="Latest captured evidence" />
                <div className="evidence-strip__meta">
                  <span className="evidence-strip__class">{CLASS_META[latestEvidence.class_name]?.label || latestEvidence.class_name}</span>
                  <span>{latestEvidence.product_id} · {latestEvidence.date}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="status-panel">
          <div className="live-feed">
            <div className="live-feed__head">
              <div>
                <strong>Live Detection Feed</strong>
                <p>Every flag, the instant it happens.</p>
              </div>
              <span className="live-feed__badge">{detections.length} this session</span>
            </div>
            <div className="live-feed__list">
              {detections.length === 0 ? (
                <div className="live-feed__empty">
                  <ShieldAlert size={20} />
                  <p>No detections yet.</p>
                  <span>Run a clip to see alerts appear here in real time.</span>
                </div>
              ) : (
                detections.slice(0, 5).map((row) => (
                  <div key={row.id} className="live-feed__item">
                    <span className="live-feed__class">{CLASS_META[row.class_name]?.label || row.class_name}</span>
                    <span className="live-feed__meta">{row.date} · {row.product_id}</span>
                    <span className="live-feed__time">{row.timestamp}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="status-panel__grid">
            <div className="status-tile">
              <span>ZONE</span>
              <strong>{hasSource ? 'Auto-locked' : 'Idle'}</strong>
            </div>
            <div className="status-tile">
              <span>CAMERA</span>
              <strong>{hasSource ? overlayCamera : '—'}</strong>
            </div>
            <div className="status-tile">
              <span>DATE</span>
              <strong>{hasSource ? (isCctvSource ? 'Live' : logDate) : '—'}</strong>
            </div>
            <div className="status-tile">
              <span>SOURCE</span>
              <strong title={activeVideoLabel}>{hasSource ? activeVideoLabel : 'None selected'}</strong>
            </div>
            <div className="status-tile">
              <span>THIS RUN</span>
              <strong>{isVideoSource ? `${runDefectTotal} defect${runDefectTotal === 1 ? '' : 's'}` : '—'}</strong>
            </div>
          </div>

          <div className="safety-monitor">
            <div className="safety-monitor__icon"><ShieldAlert size={18} /></div>
            <div className="safety-monitor__body">
              <strong>Safety &amp; Camera Monitor</strong>
              <p>{lastAlert ? `Last alert: ${lastAlert.detections?.map((d) => d.class).join(', ')}` : 'No active alert'}</p>
              {cameraNotice && (
                <p className="safety-monitor__notice">⚠ {cameraNotice.camera}: {cameraNotice.message}</p>
              )}
            </div>
            <button type="button" className="ghost-btn" onClick={handleTestAlarm}>Test alarm</button>
          </div>

          {runComplete && (
            <div className="run-complete-banner">
              <CheckCircle2 size={16} />
              <span>Run complete on {activeVideoLabel}. {runDefectTotal > 0 ? `${runDefectTotal} defect(s) logged below.` : 'No defects found.'}</span>
            </div>
          )}
        </div>
      </section>
        </div>
      </div>

      <AlertBanner alert={lastAlert} />
      <StatsSummary summary={summary} />

      <DetectionLog
        detections={detections}
        onViewEvidence={setEvidenceId}
        onResolve={handleResolve}
        onRefresh={() => refreshData()}
        loading={loadingLog}
        emptyMessage="No detections logged yet this session. Run a clip to see alerts appear here."
      />

      {evidenceId && <EvidenceModal detectionId={evidenceId} onClose={() => setEvidenceId(null)} />}

      {activeTourStep && (
        <TourOverlay
          step={tourStep}
          total={tourSteps.length}
          targetEl={activeTourStep.ref.current}
          title={activeTourStep.title}
          body={activeTourStep.body}
          isLast={tourStep === tourSteps.length}
          onNext={() => setTourStep((s) => (s < tourSteps.length ? s + 1 : 0))}
          onSkip={() => setTourStep(0)}
        />
      )}
    </div>
  );
}
